import {MqttDeviceWrapper, MqttEnergyProfile, MqttMeasures} from '../mqtt';
import {Device, DeviceType, Measure, MiniGatewayMeasureType} from 'smag-commons/cfg/connector/mqtt';
import {MqttMiniGatewayMeasureTypePipe} from '../connector.pipe';
import * as _ from 'lodash';

export class MiniGatewayMqttDeviceWrapper implements MqttDeviceWrapper<MiniGatewayMeasureType> {

    private readonly mtp: MqttMiniGatewayMeasureTypePipe;

    deviceType = DeviceType.MGW;

    maxProductionPlants = 4;

    measureTypes: Set<MiniGatewayMeasureType>;

    constructor(private readonly devId: string,
                private readonly device: Device) {
        this.mtp = new MqttMiniGatewayMeasureTypePipe();
        this.measureTypes = new Set([
            ...ABSORPTION_MEASURE_TYPES,
            ...INJECTION_MEASURE_TYPES,
            ..._.flatMap(PRODUCTION_MEASURE_TYPES, mt => Array.from(mt))
        ]);
    }

    getMqttMeasures(): MqttMeasures {
        const absorptionMeasures: string[] = [];
        const injectionMeasures: string[] = [];
        const productionMeasures: string[][] = [];
        for (let i = 0; i < this.maxProductionPlants; ++i) {
            productionMeasures[i] = [];
        }
        const measures = this.device.measures || {};
        for (const measId in measures) {
            const meas = measures[measId];
            if (ABSORPTION_MEASURE_TYPES.has(meas.mini_gateway_meas_type)) {
                absorptionMeasures.push(measId);
            } else if (INJECTION_MEASURE_TYPES.has(meas.mini_gateway_meas_type)) {
                injectionMeasures.push(measId);
            } else {
                for (let i = 0; i < this.maxProductionPlants; ++i) {
                    if (PRODUCTION_MEASURE_TYPES[i].has(meas.mini_gateway_meas_type)) {
                        productionMeasures[i].push(measId);
                    }
                }
            }
        }
        return {
            absorptionMeasures,
            injectionMeasures,
            productionMeasures
        };
    }

    getMeasureType(measId: string): MiniGatewayMeasureType {
        return (this.device.measures || {})[measId]?.mini_gateway_meas_type;
    }

    setMeasureType(measId: string, measureType: MiniGatewayMeasureType) {
        if (!this.device.measures) {
            this.device.measures = {};
        }
        if (!this.device.measures[measId]) {
            this.device.measures[measId] = {};
        }
        this.device.measures[measId].mini_gateway_meas_type = measureType;
    }

    setDefaultMeasures(profile: MqttEnergyProfile, productionPods?: string[]) {
        // NOTE: we make deep copies, as we do not want to risk overwriting the archetypes
        let measures = [];
        if (profile === MqttEnergyProfile.CONSUMER) {
            measures = _.cloneDeep(ABSORPTION_MEASURES).map(m => [m, null]);
        } else if (profile === MqttEnergyProfile.PRODUCER_PROSUMER) {
            measures = [
                ..._.cloneDeep(ABSORPTION_MEASURES).map(m => [m, 0, null]),
                ..._.cloneDeep(INJECTION_MEASURES).map(m => [m, 0, null]),
                ..._.cloneDeep(PRODUCTION_MEASURES).map(m => [m, 1, productionPods[0]])
            ];
            if (productionPods.length >= 2) {
                measures.push(..._.cloneDeep(PRODUCTION_MEASURES_2).map(m => [m, 2, productionPods[1]]));
            }
            if (productionPods.length >= 3) {
                measures.push(..._.cloneDeep(PRODUCTION_MEASURES_3).map(m => [m, 3, productionPods[2]]));
            }
            if (productionPods.length >= 4) {
                measures.push(..._.cloneDeep(PRODUCTION_MEASURES_4).map(m => [m, 4, productionPods[3]]));
            }
        }
        this.device.measures = {};
        (measures as [Measure, number, string][]).forEach(([meas, plantIndex, prodPod]) => {
            // if we do not have a production POD, we use an index in order to ensure uniqueness of `measId`
            const prodStr = plantIndex > 0 ? prodPod || `${plantIndex}` : null;
            const measId = `${this.devId}${prodStr ? '_' + prodStr : ''}_${MEASURE_SUFFIXES[meas.mini_gateway_meas_type]}`;
            this.device.measures[measId] = meas;
        });
    }

    formatMeasureType(measureType: MiniGatewayMeasureType): string {
        return this.mtp.transform(measureType);
    }
}

const ABSORPTION_MEASURES: Measure[] = [
    {
        uom: 'kWh',
        mini_gateway_meas_type: MiniGatewayMeasureType.QUARTERLY_HOUR_ABSORBED_ENERGY,
        expression: ''
    },
    {
        uom: 'kWh',
        mini_gateway_meas_type: MiniGatewayMeasureType.TOTAL_ABSORBED_ENERGY,
        expression: ''
    },
    {
        uom: 'kW',
        mini_gateway_meas_type: MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_ABSORBED_POWER,
        expression: ''
    }
];

const INJECTION_MEASURES: Measure[] = [
    {
        uom: 'kWh',
        mini_gateway_meas_type: MiniGatewayMeasureType.QUARTERLY_HOUR_INJECTED_ENERGY,
        expression: ''
    },
    {
        uom: 'kWh',
        mini_gateway_meas_type: MiniGatewayMeasureType.TOTAL_INJECTED_ENERGY,
        expression: ''
    },
    {
        uom: 'kW',
        mini_gateway_meas_type: MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_INJECTED_POWER,
        expression: ''
    }
];

const PRODUCTION_MEASURES: Measure[] = [
    {
        uom: 'kWh',
        mini_gateway_meas_type: MiniGatewayMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY,
        expression: ''
    },
    {
        uom: 'kW',
        mini_gateway_meas_type: MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER,
        expression: ''
    }
];

const PRODUCTION_MEASURES_2: Measure[] = [
    {
        uom: 'kWh',
        mini_gateway_meas_type: MiniGatewayMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY_2,
        expression: ''
    },
    {
        uom: 'kW',
        mini_gateway_meas_type: MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER_2,
        expression: ''
    }
];

const PRODUCTION_MEASURES_3: Measure[] = [
    {
        uom: 'kWh',
        mini_gateway_meas_type: MiniGatewayMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY_3,
        expression: ''
    },
    {
        uom: 'kW',
        mini_gateway_meas_type: MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER_3,
        expression: ''
    }
];

const PRODUCTION_MEASURES_4: Measure[] = [
    {
        uom: 'kWh',
        mini_gateway_meas_type: MiniGatewayMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY_4,
        expression: ''
    },
    {
        uom: 'kW',
        mini_gateway_meas_type: MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER_4,
        expression: ''
    }
];

const ABSORPTION_MEASURE_TYPES: Set<MiniGatewayMeasureType> = new Set(ABSORPTION_MEASURES.map(m => m.mini_gateway_meas_type));
const INJECTION_MEASURE_TYPES: Set<MiniGatewayMeasureType> = new Set(INJECTION_MEASURES.map(m => m.mini_gateway_meas_type));
const PRODUCTION_MEASURE_TYPES: Set<MiniGatewayMeasureType>[] = [
    new Set(PRODUCTION_MEASURES.map(m => m.mini_gateway_meas_type)),
    new Set(PRODUCTION_MEASURES_2.map(m => m.mini_gateway_meas_type)),
    new Set(PRODUCTION_MEASURES_3.map(m => m.mini_gateway_meas_type)),
    new Set(PRODUCTION_MEASURES_4.map(m => m.mini_gateway_meas_type))
];

const MEASURE_SUFFIXES: { [measType in MiniGatewayMeasureType]: string } = {
    [MiniGatewayMeasureType.QUARTERLY_HOUR_ABSORBED_ENERGY]: 'en_prel_15m',
    [MiniGatewayMeasureType.QUARTERLY_HOUR_INJECTED_ENERGY]: 'en_imm_15m',
    [MiniGatewayMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY]: 'en_gen_15m',
    [MiniGatewayMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY_2]: 'en_gen_15m',
    [MiniGatewayMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY_3]: 'en_gen_15m',
    [MiniGatewayMeasureType.QUARTERLY_HOUR_PRODUCED_ENERGY_4]: 'en_gen_15m',
    [MiniGatewayMeasureType.TOTAL_ABSORBED_ENERGY]: 'en_prel_tot',
    [MiniGatewayMeasureType.TOTAL_INJECTED_ENERGY]: 'en_imm_tot',
    [MiniGatewayMeasureType.TOTAL_PRODUCED_ENERGY]: 'en_gen_tot',
    [MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_ABSORBED_POWER]: 'pot_prel_med_15m',
    [MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_INJECTED_POWER]: 'pot_imm_med_15m',
    [MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER]: 'pot_gen_med_15m',
    [MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER_2]: 'pot_gen_med_15m',
    [MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER_3]: 'pot_gen_med_15m',
    [MiniGatewayMeasureType.QUARTERLY_HOUR_AVERAGE_PRODUCED_POWER_4]: 'pot_gen_med_15m',
};
